/** Internal snapshot ownership primitives. No application save path imports these yet. */

const MAX_BLOCK_BYTES = 256 * 1024;
export const SNAPSHOT_BLOB_BATCH_BYTES = MAX_BLOCK_BYTES;
export const SNAPSHOT_BLOB_BATCH_RECORDS = 512;
const MAX_TEXT_CODE_UNITS = MAX_BLOCK_BYTES / 2;
const MAX_CAPTURE_BYTES = 128 * 1024 * 1024;
const OWNERSHIP = Symbol('snapshot ownership');
const DEFAULT_BUDGETS = Object.freeze({
  bytes: MAX_CAPTURE_BYTES,
  records: 100_000,
  references: 1_000_000,
  dependenciesPerBlock: 100_000,
});
export const SNAPSHOT_CAPTURE_BUDGETS = DEFAULT_BUDGETS;
export type SnapshotBudgets = { readonly [K in keyof typeof DEFAULT_BUDGETS]: number };
type BudgetOverrides = Partial<SnapshotBudgets>;
function budgetsFor(overrides: BudgetOverrides = {}): SnapshotBudgets {
  for (const [key, value] of Object.entries(overrides))
    if (
      !Object.hasOwn(DEFAULT_BUDGETS, key) ||
      !Number.isSafeInteger(value) ||
      value < 0 ||
      value > DEFAULT_BUDGETS[key as keyof SnapshotBudgets]
    )
      throw new TypeError('Invalid snapshot budget');
  return Object.freeze({ ...DEFAULT_BUDGETS, ...overrides });
}

export class SnapshotLimit extends Error {
  constructor(
    message: string,
    readonly budget?: {
      readonly kind: 'bytes' | 'records';
      readonly attempted: number;
      readonly limit: number;
    },
  ) {
    super(
      budget
        ? `${message} (${budget.kind}: ${budget.attempted}/${budget.limit})`
        : message,
    );
    this.name = 'SnapshotLimit';
    if (budget) Object.freeze(budget);
  }
}

function ordinaryBuffer(buffer: ArrayBuffer): void {
  // Reject shared/resizable inputs before creating an immutable owned block.
  const length = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, 'byteLength')!
    .get!;
  try {
    length.call(buffer);
  } catch {
    throw new TypeError('Expected an ordinary ArrayBuffer');
  }
  const resizable = Object.getOwnPropertyDescriptor(
    ArrayBuffer.prototype,
    'resizable',
  )?.get;
  if (resizable?.call(buffer)) throw new TypeError('Resizable snapshot buffer');
  // Unlike byteLength, constructing this view also rejects detached buffers.
  new Uint8Array(buffer);
  if (buffer.byteLength > MAX_BLOCK_BYTES)
    throw new SnapshotLimit('Snapshot block exceeds byte budget');
}

/** The bytes never escape. Dependencies contain only atoms used by this block. */
export class SnapshotBlock {
  #buffer: ArrayBuffer;
  #view: DataView;
  readonly dependencies: readonly SnapshotBlock[];
  private constructor(buffer: ArrayBuffer, dependencies: readonly SnapshotBlock[]) {
    this.#buffer = buffer;
    this.#view = new DataView(buffer);
    this.dependencies = Object.freeze([...dependencies]);
    Object.freeze(this);
  }

  static copy(
    buffer: ArrayBuffer,
    dependencies: readonly SnapshotBlock[] = [],
  ): SnapshotBlock {
    ordinaryBuffer(buffer);
    const owned = new ArrayBuffer(buffer.byteLength);
    new Uint8Array(owned).set(new Uint8Array(buffer));
    return new SnapshotBlock(owned, dependencies);
  }

  static text(value: string): SnapshotBlock {
    if (value.length > MAX_TEXT_CODE_UNITS)
      throw new SnapshotLimit('Snapshot text atom exceeds byte budget');
    const buffer = new ArrayBuffer(value.length * 2);
    const view = new DataView(buffer);
    for (let index = 0; index < value.length; index++)
      view.setUint16(index * 2, value.charCodeAt(index), true);
    return new SnapshotBlock(buffer, []);
  }

  get byteLength(): number {
    this.#assertReadable();
    return this.#view.byteLength;
  }
  #assertReadable(): void {
    void this.#view.byteLength;
  }
  copyBytes(): Uint8Array<ArrayBuffer> {
    this.#assertReadable();
    return new Uint8Array(this.#buffer).slice();
  }
  /** Blob takes its own immutable bytes without exposing this backing buffer. */
  toBlob(): Blob {
    this.#assertReadable();
    return new Blob([this.#buffer]);
  }
  /** One bounded immutable copy, without exposing or consuming backing buffers. */
  static toBlobBatch(blocks: readonly SnapshotBlock[]): Blob {
    if (blocks.length > SNAPSHOT_BLOB_BATCH_RECORDS)
      throw new SnapshotLimit('Snapshot Blob batch exceeds record budget');
    const parts: ArrayBuffer[] = [];
    let bytes = 0;
    for (const block of blocks) {
      if (parts.length >= SNAPSHOT_BLOB_BATCH_RECORDS)
        throw new SnapshotLimit('Snapshot Blob batch exceeds record budget');
      block.#assertReadable();
      bytes += block.#buffer.byteLength;
      if (bytes > SNAPSHOT_BLOB_BATCH_BYTES)
        throw new SnapshotLimit('Snapshot Blob batch exceeds byte budget');
      parts.push(block.#buffer);
    }
    return new Blob(parts);
  }
  *sameBytes(other: SnapshotBlock): Generator<void, boolean> {
    if (this === other) return true;
    this.#assertReadable();
    other.#assertReadable();
    if (this.#buffer.byteLength !== other.#buffer.byteLength) return false;
    const a = new Uint8Array(this.#buffer),
      b = new Uint8Array(other.#buffer);
    for (let start = 0; start < a.length; start += 32768) {
      for (let i = start; i < Math.min(start + 32768, a.length); i++)
        if (a[i] !== b[i]) return false;
      yield;
    }
    return true;
  }
  readUint32(offset: number): number {
    return this.#view.getUint32(offset, true);
  }
  readText(): string {
    return this.readTextRange(0, this.byteLength / 2);
  }
  readTextRange(offset: number, codeUnits: number): string {
    this.#assertReadable();
    if (
      !Number.isSafeInteger(offset) ||
      !Number.isSafeInteger(codeUnits) ||
      offset < 0 ||
      codeUnits < 0 ||
      offset % 2 ||
      offset + codeUnits * 2 > this.#buffer.byteLength
    )
      throw new TypeError('Invalid UTF16 text range');
    const view = this.#view;
    const fragments: string[] = [];
    // Construct flat UTF-16 chunks. Character-by-character concatenation can
    // retain a chain of strings for every decoded runtime identity until the
    // hydration DTO is released. A bounded code-unit array also preserves lone
    // surrogates, unlike a text decoder that replaces malformed Unicode.
    const units = new Uint16Array(Math.min(4096, codeUnits));
    const end = offset + codeUnits * 2;
    for (let start = offset; start < end; start += 8192) {
      const count = Math.min(4096, (end - start) / 2);
      for (let index = 0; index < count; index++)
        units[index] = view.getUint16(start + index * 2, true);
      fragments.push(String.fromCharCode(...units.subarray(0, count)));
    }
    return fragments.join('');
  }

  /** Only a whole capture can move its buffers; never expose partial-block transfer. */
  static consume(
    token: symbol,
    roots: readonly SnapshotBlock[],
    budgets: SnapshotBudgets,
  ): SnapshotPacket {
    if (token !== OWNERSHIP) throw new TypeError('Use complete capture transfer');
    const blocks: SnapshotBlock[] = [],
      indexes = new Map<SnapshotBlock, number>();
    const visit = (block: SnapshotBlock): number => {
      const prior = indexes.get(block);
      if (prior !== undefined) return prior;
      block.#assertReadable();
      const index = blocks.length;
      indexes.set(block, index);
      blocks.push(block);
      for (const dependency of block.dependencies) visit(dependency);
      return index;
    };
    const rootIndexes = roots.map(visit);
    const records = blocks.map((block) => ({
      buffer: block.#buffer,
      dependencies: block.dependencies.map((dependency) => indexes.get(dependency)!),
    }));
    const packet = { version: 1 as const, roots: rootIndexes, records };
    // Reject before any detach; sender-produced packets obey receiver budgets.
    validatePacket(packet, budgets);
    // Transfer detaches every sender alias, including shared text atoms. This
    // consumes the complete capture, not one chip selected from a live capture.
    return structuredClone(packet, {
      transfer: records.map((record) => record.buffer),
    });
  }

  /** Consume a received packet. No output block retains the packet/index table. */
  static receive(
    packet: SnapshotPacket,
    budgets?: BudgetOverrides,
  ): readonly SnapshotBlock[] {
    validatePacket(packet, budgetsFor(budgets));
    const moved = structuredClone(packet, {
      transfer: packet.records.map((record) => record.buffer),
    });
    const built = new Map<number, SnapshotBlock>();
    const build = (index: number): SnapshotBlock => {
      const previous = built.get(index);
      if (previous) return previous;
      const record = moved.records[index];
      const block = new SnapshotBlock(record.buffer, record.dependencies.map(build));
      built.set(index, block);
      return block;
    };
    return Object.freeze(moved.roots.map(build));
  }
}

/** Internal, versioned ownership packet, not a persisted format or node codec. */
export interface SnapshotPacket {
  version: 1;
  roots: number[];
  records: { buffer: ArrayBuffer; dependencies: number[] }[];
}

export interface SnapshotLayout {
  roots: number[];
  records: { bytes: number; dependencies: number[] }[];
}

/** Validate counts and dependency topology before a file reader allocates buffers. */
export function validateSnapshotLayout(
  layout: SnapshotLayout,
  overrides?: Partial<SnapshotBudgets>,
): void {
  validateLayout(layout, budgetsFor(overrides));
}

function validatePacket(packet: SnapshotPacket, budgets: SnapshotBudgets): void {
  if (
    packet.version !== 1 ||
    !Array.isArray(packet.records) ||
    !Array.isArray(packet.roots)
  )
    throw new TypeError('Invalid snapshot packet');
  const buffers = new Set<ArrayBuffer>();
  const records = packet.records.map((record) => {
    ordinaryBuffer(record.buffer);
    if (buffers.has(record.buffer))
      throw new TypeError('Duplicate snapshot backing buffer');
    buffers.add(record.buffer);
    return { bytes: record.buffer.byteLength, dependencies: record.dependencies };
  });
  validateLayout({ roots: packet.roots, records }, budgets);
}

function validateLayout(layout: SnapshotLayout, budgets: SnapshotBudgets): void {
  if (!Array.isArray(layout.records) || !Array.isArray(layout.roots))
    throw new TypeError('Invalid snapshot layout');
  if (layout.records.length > budgets.records || layout.roots.length > budgets.records)
    throw new SnapshotLimit('Too many snapshot blocks');
  if (new Set(layout.roots).size !== layout.roots.length)
    throw new TypeError('Duplicate snapshot roots');
  let bytes = 0,
    references = 0;
  for (const record of layout.records) {
    if (
      !record ||
      !Number.isSafeInteger(record.bytes) ||
      record.bytes < 0 ||
      record.bytes > MAX_BLOCK_BYTES
    )
      throw new TypeError('Invalid snapshot block size');
    if (
      !Array.isArray(record.dependencies) ||
      record.dependencies.length > budgets.dependenciesPerBlock
    )
      throw new TypeError('Invalid snapshot dependencies');
    bytes += record.bytes;
    references += record.dependencies.length;
    if (bytes > budgets.bytes || references > budgets.references)
      throw new SnapshotLimit('Snapshot packet exceeds total budget');
  }
  const visited = new Set<number>(),
    visiting = new Set<number>();
  const visit = (index: number, depth: number): void => {
    if (!Number.isSafeInteger(index) || index < 0 || index >= layout.records.length)
      throw new TypeError('Invalid snapshot block index');
    if (depth > 64) throw new SnapshotLimit('Snapshot dependency depth exceeds budget');
    if (visiting.has(index)) throw new TypeError('Cyclic snapshot dependencies');
    if (visited.has(index)) return;
    visiting.add(index);
    for (const dependency of layout.records[index].dependencies)
      visit(dependency, depth + 1);
    visiting.delete(index);
    visited.add(index);
  };
  for (const root of layout.roots) visit(root, 0);
  if (visited.size !== layout.records.length)
    throw new TypeError('Unreachable snapshot blocks');
}

/** Capture-local pool. finish/abandon erase every builder-owned reference. */
export class SnapshotBlockBuilder {
  #texts = new Map<string, SnapshotBlock>();
  #roots: SnapshotBlock[] = [];
  #closed = false;
  #bytes = 0;
  #references = 0;
  #budgets: SnapshotBudgets;
  constructor(budgets?: BudgetOverrides) {
    this.#budgets = budgetsFor(budgets);
  }
  #assertOpen(): void {
    if (this.#closed) throw new Error('Snapshot builder is closed');
  }
  #captureLimit(kind: 'bytes' | 'records', attempted: number): SnapshotLimit {
    return new SnapshotLimit('Snapshot capture exceeds total budget', {
      kind,
      attempted,
      limit: this.#budgets[kind],
    });
  }

  addRoot(columns: ArrayBuffer, identityParts: readonly string[]): SnapshotBlock {
    this.#assertOpen();
    ordinaryBuffer(columns);
    if (
      identityParts.length > this.#budgets.dependenciesPerBlock ||
      this.#references + identityParts.length > this.#budgets.references
    )
      throw new SnapshotLimit('Snapshot dependencies exceed budget');
    if (this.#bytes + columns.byteLength > this.#budgets.bytes)
      throw this.#captureLimit('bytes', this.#bytes + columns.byteLength);
    if (this.#roots.length + this.#texts.size >= this.#budgets.records)
      throw this.#captureLimit('records', this.#roots.length + this.#texts.size + 1);
    const atoms = identityParts.map((text) => {
      let atom = this.#texts.get(text);
      if (!atom) {
        if (this.#bytes + columns.byteLength + text.length * 2 > this.#budgets.bytes)
          throw this.#captureLimit(
            'bytes',
            this.#bytes + columns.byteLength + text.length * 2,
          );
        if (this.#roots.length + this.#texts.size + 1 >= this.#budgets.records)
          throw this.#captureLimit('records', this.#roots.length + this.#texts.size + 2);
        atom = SnapshotBlock.text(text);
        this.#texts.set(text, atom);
        this.#bytes += atom.byteLength;
      }
      return atom;
    });
    if (this.#bytes + columns.byteLength > this.#budgets.bytes)
      throw this.#captureLimit('bytes', this.#bytes + columns.byteLength);
    if (this.#roots.length >= this.#budgets.records)
      throw this.#captureLimit('records', this.#roots.length + 1);
    const root = SnapshotBlock.copy(columns, atoms);
    this.#bytes += root.byteLength;
    this.#references += identityParts.length;
    this.#roots.push(root);
    return root;
  }

  finish(): SnapshotCapture {
    this.#assertOpen();
    const capture = new SnapshotCapture(OWNERSHIP, this.#roots, this.#budgets);
    this.abandon();
    return capture;
  }
  abandon(): void {
    this.#closed = true;
    this.#texts.clear();
    this.#roots = [];
    this.#bytes = 0;
    this.#references = 0;
  }
}

/** The transfer unit is the complete capture; roots can be retained individually after receive. */
export class SnapshotCapture {
  #roots: readonly SnapshotBlock[];
  #consumed = false;
  #budgets: SnapshotBudgets;
  constructor(token: symbol, roots: readonly SnapshotBlock[], budgets: SnapshotBudgets) {
    if (token !== OWNERSHIP) throw new TypeError('Use snapshot builder');
    this.#budgets = budgets;
    this.#roots = Object.freeze([...roots]);
  }
  get roots(): readonly SnapshotBlock[] {
    if (this.#consumed) throw new Error('Snapshot capture was consumed');
    return this.#roots;
  }
  consumeForTransfer(): SnapshotPacket {
    const packet = SnapshotBlock.consume(OWNERSHIP, this.roots, this.#budgets);
    this.#consumed = true;
    this.#roots = [];
    return packet;
  }
}

export class UnsupportedSnapshotState extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedSnapshotState';
  }
}

export type SnapshotCaptureResult =
  | { kind: 'compact'; capture: SnapshotCapture }
  | { kind: 'legacy-required'; reason: string };

/**
 * Caller must have a replayable source and choose fallback in this same epoch.
 * Expected unsupported/limit cases release scratch ownership before returning.
 * Invariant/programming errors propagate; they never silently select fallback.
 */
export function tryCaptureSnapshot(
  build: (builder: SnapshotBlockBuilder) => void,
  budgets?: BudgetOverrides,
): SnapshotCaptureResult {
  const builder = new SnapshotBlockBuilder(budgets);
  try {
    const result = build(builder);
    if (result !== undefined) throw new TypeError('Snapshot capture must be synchronous');
    return { kind: 'compact', capture: builder.finish() };
  } catch (error) {
    builder.abandon();
    if (error instanceof SnapshotLimit || error instanceof UnsupportedSnapshotState)
      return { kind: 'legacy-required', reason: error.message };
    throw error;
  }
}

/** Capacity of unique buffers, including dependencies; excludes JS object overhead. */
export function snapshotByteLength(roots: readonly SnapshotBlock[]): number {
  const seen = new Set<SnapshotBlock>();
  let bytes = 0;
  const visit = (block: SnapshotBlock): void => {
    if (seen.has(block)) return;
    seen.add(block);
    bytes += block.byteLength;
    for (const dependency of block.dependencies) visit(dependency);
  };
  for (const root of roots) visit(root);
  return bytes;
}
